import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { FaHeart, FaRegHeart } from "react-icons/fa";

const WishlistToastContext = createContext(null);

const TOAST_DURATION = 3000;

export const WishlistToastProvider = ({ children }) => {
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const showToast = useCallback((action, label) => {
    if (!action || !label) return;
    
    setFeedback({ action, label });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFeedback(null), TOAST_DURATION);
  }, []);

  const WishlistToast = () => {
    if (!feedback) return null;
    
    const isAdded = feedback.action === "added";
    const message = isAdded ? "Added to wishlist" : "Removed from wishlist";
    
    return (
      <>
        <div
          className="wishlist-toast-container"
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            zIndex: 12000,
            pointerEvents: "none",
            animation: "toastSlideIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          }}
        >
          <div
            className="wishlist-toast"
            style={{
              background: isAdded
                ? "var(--theme-gradient-state-success)"
                : "var(--theme-gradient-state-error)",
              color: "var(--theme-surface-white)",
              padding: "16px 20px",
              borderRadius: "16px",
              boxShadow: isAdded ? "var(--theme-toast-shadow-success)" : "var(--theme-toast-shadow-error)",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
              maxWidth: "380px",
              minWidth: "280px",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Animated background glow */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isAdded
                  ? "radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.15) 0%, transparent 70%)"
                  : "radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            
            {/* Icon container with animation */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                flexShrink: 0,
                animation: "heartBeat 0.6s ease-out",
              }}
            >
              {isAdded ? (
                <FaHeart
                  style={{
                    fontSize: "20px",
                    color: "var(--theme-surface-white)",
                    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
                  }}
                />
              ) : (
                <FaRegHeart
                  style={{
                    fontSize: "20px",
                    color: "var(--theme-surface-white)",
                    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
                  }}
                />
              )}
            </div>
            
            {/* Text content */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "15px",
                  letterSpacing: "0.3px",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              >
                {message}
              </span>
              <span
                style={{
                  opacity: 0.95,
                  fontSize: "13px",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              >
                {feedback.label}
              </span>
            </div>
            
            {/* Progress bar */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "rgba(255, 255, 255, 0.3)",
                borderRadius: "0 0 16px 16px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.6)",
                  width: "100%",
                  animation: `progressBar ${TOAST_DURATION}ms linear forwards`,
                }}
              />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes toastSlideIn {
            0% {
              transform: translateX(120%) scale(0.8);
              opacity: 0;
            }
            60% {
              transform: translateX(-5px) scale(1.02);
            }
            100% {
              transform: translateX(0) scale(1);
              opacity: 1;
            }
          }
          
          @keyframes heartBeat {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes progressBar {
            0% {
              width: 100%;
            }
            100% {
              width: 0%;
            }
          }
          
          .wishlist-toast-container {
            will-change: transform, opacity;
          }
          
          .wishlist-toast {
            will-change: transform;
          }
        `}</style>
      </>
    );
  };

  return (
    <WishlistToastContext.Provider value={{ showToast }}>
      {children}
      <WishlistToast />
    </WishlistToastContext.Provider>
  );
};

export const useWishlistToastContext = () => {
  const context = useContext(WishlistToastContext);
  if (!context) {
    // Return a no-op function if context is not available (for graceful degradation)
    return { showToast: () => {} };
  }
  return context;
};

